import { useEffect, useMemo, useState } from 'react';
import { findUserDirectoryUser, searchUserDirectory } from '../lib/api';
import { errorMessage, isMtibEmail } from '../lib/utils';
import type { DirectoryUser } from '../types';
import { useToast } from '../context/ToastContext';

export default function CoHostPicker({ values, onChange, hostEmail }: { values: string[]; onChange: (values: string[]) => void; hostEmail: string }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<DirectoryUser[]>([]);
  const [selectedProfiles, setSelectedProfiles] = useState<Record<string, DirectoryUser>>({});
  const [searching, setSearching] = useState(false);
  const { showToast } = useToast();
  const selected = useMemo(() => new Set(values.map((value) => value.toLowerCase())), [values]);
  const selectedKey = useMemo(() => values.map((value) => value.toLowerCase()).join('|'), [values]);

  useEffect(() => {
    let cancelled = false;
    const missing = values.filter((email) => !selectedProfiles[email.toLowerCase()]);
    if (!missing.length) return () => { cancelled = true; };
    void Promise.all(missing.map((email) => findUserDirectoryUser(email))).then((matches) => {
      if (cancelled) return;
      setSelectedProfiles((current) => {
        const next = { ...current };
        matches.forEach((match) => { if (match) next[match.email.toLowerCase()] = match; });
        return next;
      });
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [selectedKey]);

  useEffect(() => {
    if (query.trim().length < 3) { setResults([]); setSearching(false); return; }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setSearching(true);
      try {
        const matches = await searchUserDirectory(query);
        if (!cancelled) setResults(matches.filter((item) => item.is_internal && isMtibEmail(item.email) && item.email.toLowerCase() !== hostEmail.toLowerCase() && !selected.has(item.email.toLowerCase())));
      } catch (error) {
        if (!cancelled) showToast(errorMessage(error), 'error');
      } finally { if (!cancelled) setSearching(false); }
    }, 260);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [hostEmail, query, selected, showToast]);

  const add = (user: DirectoryUser) => {
    const email = user.email.toLowerCase();
    if (!user.is_internal || !isMtibEmail(email) || email === hostEmail.toLowerCase() || selected.has(email)) return;
    onChange([...values, email]);
    setQuery('');
    setResults([]);
  };

  return <div className="cohost-picker">
    {values.length ? <div className="cohost-list">{values.map((email) => {
      const user = selectedProfiles[email.toLowerCase()];
      const label = user?.full_name ?? 'MTIB staff';
      return <span className="cohost-chip" key={email}><span><em>Co-Host</em> · {label}</span><button type="button" aria-label={`Keluarkan Co-Host ${label}`} onClick={() => onChange(values.filter((item) => item !== email))}>×</button></span>;
    })}</div> : null}
    <div className="attendee-search-wrap">
      <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Cari pegawai MTIB…" autoComplete="off" />
      {query.trim().length >= 3 ? <div className="directory-results">{searching ? <div className="directory-message">Sedang mencari pegawai MTIB…</div> : results.length ? results.map((user) => <button type="button" className="directory-result" key={user.email} onMouseDown={(event) => event.preventDefault()} onClick={() => add(user)}><strong>{user.full_name}</strong><span>{user.email}</span><small>{user.department_org}</small></button>) : <div className="directory-message">Tiada pegawai MTIB yang layak ditemui.</div>}</div> : null}
    </div>
    <small><em>Co-Host</em> hanya untuk pegawai MTIB. <em>Host</em> semasa tidak boleh dipilih.</small>
  </div>;
}
